import React, { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import { useSearchParams } from "react-router-dom"
import { useActiveClinic } from "../../context/ClinicContext"
import { useClinicPlanSnapshot } from "../../features/subscriptions/hooks/useClinicPlanSnapshot"
import { useClinicSubscription } from "../../features/subscriptions/hooks/useClinicSubscription"
import SettingsModuleScaffold from "./SettingsModuleScaffold"
import SettingsPage from "../SettingsPage"
import UpgradePlanPage from "../../features/subscriptions/pages/UpgradePlanPage"
import SubscriptionAdminPage from "../../features/subscriptions/pages/SubscriptionAdminPage"
import { DEFAULT_USER_ID } from "../../config/appConfig"
import {
  resolveClinicLabel,
  SectionCard,
  MetricCard,
  InsightCard,
  TopStateCard,
  actionButtonStyle
} from "./settingsShared"

const tabs = [
  { key: "general", label: "General" },
  { key: "plan", label: "Plan" },
  { key: "subscriptions", label: "Suscripciones" }
]

export default function SystemAccountPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { activeClinicId } = useActiveClinic()

  const rawTab = searchParams.get("tab")
  const activeTab =
    rawTab === "plan" || rawTab === "subscriptions" || rawTab === "general"
      ? rawTab
      : "general"

  const clinicId =
    activeClinicId && activeClinicId !== "ALL" ? activeClinicId : null

  const userId =
    localStorage.getItem("odontoflow_user_id") || DEFAULT_USER_ID

  const [clinicRows, setClinicRows] = useState([])
  const [error, setError] = useState("")

  const { data: planSnapshot } = useClinicPlanSnapshot(clinicId)
  const { subscription } = useClinicSubscription(clinicId)

  const currentPlanName = planSnapshot?.plan?.name || "Sin plan activo"
  const accountName = planSnapshot?.accountName || "tu cuenta"

  const doctorsCount = Number(planSnapshot?.usage?.doctorsCount || 0)
  const usersCount = Number(planSnapshot?.usage?.usersCount || 0)

  const maxDoctors = planSnapshot?.plan?.maxDoctors
  const maxUsers = planSnapshot?.plan?.maxUsers

  const isReadOnly = Boolean(subscription?.access?.isReadOnly)
  const readOnlyReason =
    subscription?.access?.reason || "La clínica está en modo solo lectura."

  const clinicsCount = clinicRows.length
  const activeClinicLabel = useMemo(
    () => resolveClinicLabel(clinicRows, clinicId),
    [clinicRows, clinicId]
  )
  const showActiveClinicMetric = clinicsCount > 1 && !!activeClinicLabel

  const loadClinics = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/multi-clinic/user-clinics", {
        params: { userId }
      })
      setClinicRows(Array.isArray(data) ? data : [])
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudieron cargar las clínicas"
      setError(Array.isArray(message) ? message[0] : message)
      setClinicRows([])
    }
  }, [userId])

  useEffect(() => {
    loadClinics()
  }, [loadClinics])

  const generalStatus = useMemo(() => {
    if (!clinicId) {
      return {
        label: "General",
        tone: "info",
        detail:
          "La configuración general puede revisarse aunque no haya una clínica específica seleccionada."
      }
    }

    return {
      label: "Configurable",
      tone: "success",
      detail:
        "La clínica activa ya puede revisar parámetros globales y ajustes administrativos."
    }
  }, [clinicId])

  const planStatus = useMemo(() => {
    if (!currentPlanName || currentPlanName === "Sin plan activo") {
      return {
        label: "Revisar",
        tone: "warning",
        detail:
          "Conviene revisar el plan activo, ya que no se detecta uno claramente asociado."
      }
    }

    if (isReadOnly) {
      return {
        label: "Restringido",
        tone: "danger",
        detail:
          "La cuenta está en modo solo lectura; el estado comercial del plan requiere atención."
      }
    }

    return {
      label: "Activo",
      tone: "info",
      detail: `La cuenta opera actualmente sobre el plan ${currentPlanName}.`
    }
  }, [currentPlanName, isReadOnly])

  const subscriptionsStatus = useMemo(() => {
    if (isReadOnly) {
      return {
        label: "Seguimiento",
        tone: "warning",
        detail:
          "La administración de suscripciones conviene revisarse porque la clínica ya presenta restricciones operativas."
      }
    }

    return {
      label: "Disponible",
      tone: "success",
      detail:
        "La administración de suscripciones está disponible para seguimiento y control."
    }
  }, [isReadOnly])

  const handleTabChange = (tab) => {
    setSearchParams({ tab })
  }

  const actions = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <button
        onClick={() => handleTabChange("general")}
        style={{
          ...actionButtonStyle,
          background: activeTab === "general" ? "#ffffff" : "rgba(255,255,255,0.08)",
          color: activeTab === "general" ? "#0f172a" : "#ffffff",
          border:
            activeTab === "general"
              ? "1px solid rgba(255,255,255,0.40)"
              : "1px solid rgba(255,255,255,0.16)"
        }}
      >
        Ver general
      </button>

      <button
        onClick={() => handleTabChange("plan")}
        style={{
          ...actionButtonStyle,
          background: activeTab === "plan" ? "#ffffff" : "rgba(255,255,255,0.08)",
          color: activeTab === "plan" ? "#0f172a" : "#ffffff",
          border:
            activeTab === "plan"
              ? "1px solid rgba(255,255,255,0.40)"
              : "1px solid rgba(255,255,255,0.16)"
        }}
      >
        Ver plan
      </button>

      <button
        onClick={() => handleTabChange("subscriptions")}
        style={{
          ...actionButtonStyle,
          background:
            activeTab === "subscriptions" ? "#ffffff" : "rgba(255,255,255,0.08)",
          color: activeTab === "subscriptions" ? "#0f172a" : "#ffffff",
          border:
            activeTab === "subscriptions"
              ? "1px solid rgba(255,255,255,0.40)"
              : "1px solid rgba(255,255,255,0.16)"
        }}
      >
        Ver suscripciones
      </button>
    </div>
  )

  return (
    <SettingsModuleScaffold
      eyebrow="Sistema y cuenta"
      title="Sistema y cuenta"
      subtitle="Administra configuración general, plan activo y herramientas de suscripción bajo una sola lectura administrativa."
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      actions={actions}
    >
      {isReadOnly ? (
        <TopStateCard tone="danger" title="Modo solo lectura">
          {readOnlyReason}
        </TopStateCard>
      ) : null}

      {error ? (
        <TopStateCard tone="danger" title="Error">
          {error}
        </TopStateCard>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 12
        }}
      >
        <MetricCard
          label="Plan"
          value={currentPlanName}
          helper={accountName}
          tone={planStatus.tone}
        />

        <MetricCard
          label="Doctores"
          value={String(doctorsCount)}
          helper={
            maxDoctors === null || maxDoctors === undefined
              ? "Límite ilimitado"
              : `Límite ${maxDoctors}`
          }
          tone="info"
        />

        <MetricCard
          label="Usuarios"
          value={String(usersCount)}
          helper={
            maxUsers === null || maxUsers === undefined
              ? "Límite ilimitado"
              : `Límite ${maxUsers}`
          }
          tone="info"
        />

        {showActiveClinicMetric ? (
          <MetricCard
            label="Clínica activa"
            value={activeClinicLabel}
            helper="Contexto actual"
            tone="neutral"
          />
        ) : null}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 12
        }}
      >
        <InsightCard
          title="Configuración general"
          status={generalStatus.label}
          tone={generalStatus.tone}
          detail={generalStatus.detail}
          ctaLabel="Ir a general"
          onClick={() => handleTabChange("general")}
        />

        <InsightCard
          title="Plan y upgrade"
          status={planStatus.label}
          tone={planStatus.tone}
          detail={planStatus.detail}
          ctaLabel="Ir a plan"
          onClick={() => handleTabChange("plan")}
        />

        <InsightCard
          title="Administración de suscripciones"
          status={subscriptionsStatus.label}
          tone={subscriptionsStatus.tone}
          detail={subscriptionsStatus.detail}
          ctaLabel="Ir a suscripciones"
          onClick={() => handleTabChange("subscriptions")}
        />
      </div>

      {activeTab === "general" ? (
        <SectionCard
          title="Parámetros globales"
          subtitle="Aquí se conserva la configuración general actual, ahora ubicada dentro del frente correcto."
        >
          <SettingsPage />
        </SectionCard>
      ) : null}

      {activeTab === "plan" ? (
        <SectionCard
          title="Plan y upgrade"
          subtitle="Aquí se mantiene la gestión actual del plan, ya alineada a la nueva arquitectura."
        >
          <UpgradePlanPage />
        </SectionCard>
      ) : null}

      {activeTab === "subscriptions" ? (
        <SectionCard
          title="Suscripciones"
          subtitle="Aquí se conserva la administración de suscripciones para perfiles con acceso."
        >
          <SubscriptionAdminPage />
        </SectionCard>
      ) : null}
    </SettingsModuleScaffold>
  )
}